import {l} from "types/typescriptdefinisjoner_lederplan";
import {BasisRessurs} from "felles/infrastruktur/BasisRessurs";
import {gettextCatalog} from "angular-gettext";
import _ from "lodash";
import {RessursFeilhåndtering} from "../RessursFeilhåndtering";
import {regNavn} from "../tredjepartsTjenestenavn";
import moment from "moment";
import {Debugger} from "debug";
import {tilLokalDatoIgnorerTs} from "../Datoer";

const debug: Debugger = require('debug')('OppgaveRessurs');

export enum OppgavestatusBeregnet {ikkeoverskredet, overskredet, overskrides, utfoert, ingenfrist}

export class OppgaveRessurs extends BasisRessurs<l.OppgaveModell> {

    static regNavn = "OppgaveRessurs";

    static forInjisering() {
        return [
            regNavn.ng.$resource,
            RessursFeilhåndtering.regNavn,
            regNavn.gettext.gettextCatalog,
            OppgaveRessurs
        ]
    }

    constructor($resource: ng.resource.IResourceService, ressursFeilhåndtering: RessursFeilhåndtering, gettextCatalog: gettextCatalog) {
        super("oppgave",
            $resource,
            ressursFeilhåndtering,
            gettextCatalog,
            {
                oppgaveAggregater: {method: 'POST', url: "/api/oppgave/oppgaveAggregater", params: {}},
                opprettKommentar: {method: 'PUT', url: "/api/oppgave/:oppgaveId/opprettKommentar", params: {id: "", oppgaveId: "@oppgaveId"}},
                finnOppgaver: {method: 'POST', url: "/api/oppgave/finnOppgaver", params: {id: ""}, isArray: true},
                finnOppgaverPaginert: {method: 'POST', url: "/api/oppgave/finnOppgaverPaginert", params: {id: ""}},
                finnOppgaverMedFristIPerioden: {method: 'GET', url: "/api/oppgave/finnOppgaverMedFristIPerioden", params: {id: ""}, isArray: true},
                finnUtførteOppgaverMedFristIPerioden: {method: 'GET', url: "/api/oppgave/finnUtfoerteOppgaverMedFristIPerioden", params: {id: ""}, isArray: true},
                finnIkkeUtførteOppgaverMedForfallFør: {method: 'GET', url: "/api/oppgave/finnIkkeUtfoerteOppgaverMedForfallFoer", params: {id: ""}, isArray: true},
                finnIkkeUtførteOppgaverUtenFrist: {method: 'GET', url: "/api/oppgave/finnIkkeUtfoerteOppgaverUtenFrist", params: {id: ""}, isArray: true},
                finnOppgaverUtenFrist: {method: 'GET', url: "/api/oppgave/finnOppgaverUtenFrist", params: {id: ""}, isArray: true},
                finnAndresOppgaverMedFristIPerioden: {method: 'GET', url: "/api/oppgave/finnAndresOppgaverMedFristIPerioden", params: {id: ""}, isArray: true},
                finnAndresOppgaverUtenFrist: {method: 'GET', url: "/api/oppgave/finnAndresOppgaverUtenFrist", params: {id: ""}, isArray: true},
                finnAndresIkkeUtførteOppgaverMedForfallFør: {method: 'GET', url: "/api/oppgave/finnAndresIkkeUtfoerteOppgaverMedForfallFoer", params: {id: ""}, isArray: true},
                aggregerteOppgaverMedFristIPerioden: {method: 'GET', url: "/api/oppgave/aggregerteOppgaverMedFristIPerioden", params: {id: ""}},
                antallOppgaverPerBruker: {method: 'GET', url: "/api/oppgave/antallOppgaverPerBruker", params: {id: ""}, isArray: true},
                settStatus: {method: 'POST', url: "/api/oppgave/:id/settStatus"},
                antallForfalteOppgaverForBruker: {method: 'GET', url: "/api/oppgave/antallForfalteOppgaverForBruker", params: {id: ""}}
            });
    }

    //offentlige metoder

    antallForfalteOppgaverForBruker(vedOk: (antall: l.AntallForfalteOppgaver) => void = _.noop) {
        debug("antallForfalteOppgaverForBruker");
        return this.ressurs["antallForfalteOppgaverForBruker"](
            {},
            vedOk,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste antall forfalte oppgaver"))
        );
    }

    settStatus(spesifikasjon: l.OppgaveStatusSpesifikasjon): l.OppgaveModell {
        debug("settStatus", spesifikasjon);
        return this.ressurs["settStatus"](
            spesifikasjon,
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke oppdatere oppgavestatus"))
        );
    }

    opprettKommentar(oppgaveId, spesifikasjon, vedOk: (kommentar: l.KommentarModell) => void = _.noop): l.KommentarModell {
        debug("opprettKommentar", oppgaveId, spesifikasjon);
        return this.ressurs["opprettKommentar"](
            _.defaults({oppgaveId: oppgaveId}, spesifikasjon),
            vedOk,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke opprette kommentar under oppgave"))
        );
    }

    oppgaveAggregater(spesifikasjon: l.FinnOppgaverSpesifikasjon): l.AngularRessurs<any> {
        debug("oppgaveAggregater", spesifikasjon);
        return this.ressurs['oppgaveAggregater'](
            spesifikasjon,
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste oppgave aggregater")));
    }

    finnOppgaver(spesifikasjon: l.FinnOppgaverSpesifikasjon): l.OppgaveModell[] {
        debug("finnOppgaver", spesifikasjon);
        return this.ressurs["finnOppgaver"](
            spesifikasjon,
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finne oppgaver"))
        );
    }

    finnOppgaverPaginert(spesifikasjon: l.FinnOppgaverSpesifikasjon): l.OppgaverMedPaginering {
        debug("finnOppgaver", spesifikasjon);
        return this.ressurs["finnOppgaverPaginert"](
            spesifikasjon,
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finne oppgaver"))
        );
    }

    /** Henter dine egne oppgaver */
    finnIkkeUtførteOppgaverMedForfallFør(forfallsdato: Date, vedOk: (oppgaver: l.OppgaveModell[]) => void = _.noop): l.OppgaveModell[] {
        debug("finnIkkeUtførteOppgaverMedForfallFør", forfallsdato);
        return this.ressurs["finnIkkeUtførteOppgaverMedForfallFør"](
            {forfallsdato: forfallsdato},
            vedOk,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finne oppgaver"))
        );
    }

    finnIkkeUtførteOppgaverUtenFrist(vedOk: (oppgaver: l.OppgaveModell[]) => void = _.noop) {
        debug("finnIkkeUtførteOppgaverUtenFrist");
        return this.ressurs["finnIkkeUtførteOppgaverUtenFrist"](
            {},
            vedOk,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finne oppgaver"))
        );
    }

    finnOppgaverMedFristIPerioden(start: Date, slutt: Date, medUtforte = false, vedOk: (oppgaver: l.OppgaveModell[]) => void = _.noop): any[] {
        debug("finnOppgaverMedFristIPerioden", start, slutt);
        return this.ressurs["finnOppgaverMedFristIPerioden"](
            {start: start, slutt: slutt, medUtforte: medUtforte},
            vedOk,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finne oppgaver"))
        );
    }

    finnUtførteOppgaverMedFristIPerioden(start: Date, slutt: Date): l.OppgaveModell[] {
        debug("finnUtførteOppgaverMedFristIPerioden", start, slutt);
        return this.ressurs["finnUtførteOppgaverMedFristIPerioden"](
            {start: start, slutt: slutt},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finne oppgaver"))
        );
    }

    finnAndresIkkeUtførteOppgaverMedForfallFør(forfallsdato: Date, vedOk: (oppgaver: l.OppgaveModell[]) => void = _.noop): l.OppgaveModell[] {
        debug("finnAndresIkkeUtførteOppgaverMedForfallFør", forfallsdato);
        return this.ressurs["finnAndresIkkeUtførteOppgaverMedForfallFør"](
            {forfallsdato: forfallsdato},
            vedOk,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finne oppgaver"))
        );
    }

    finnAndresOppgaverMedFristIPerioden(start: Date, slutt: Date, medUtførte = false, vedOk: (oppgaver: l.OppgaveModell[]) => void = _.noop): any[] {
        debug("finnAndresOppgaverMedFristIPerioden", start, slutt, medUtførte);
        return this.ressurs["finnAndresOppgaverMedFristIPerioden"](
            {start: start, slutt: slutt, medUtforte: medUtførte},
            vedOk,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finne oppgaver"))
        );
    }

    finnAndresOppgaverUtenFrist(medUtførte = true, vedOk: (oppgaver: l.OppgaveModell[]) => void = _.noop): any[] {
        debug("finnAndresOppgaverUtenFrist");
        return this.ressurs["finnAndresOppgaverUtenFrist"](
            {medUtforte: medUtførte},
            vedOk,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finne oppgaver"))
        );
    }

    finnOppgaverUtenFrist(medUtførte = true, vedOk: (oppgaver: l.OppgaveModell[]) => void = _.noop): any[] {
        debug("finnOppgaverUtenFrist");
        return this.ressurs["finnOppgaverUtenFrist"](
            {medUtforte: medUtførte},
            vedOk,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finne oppgaver"))
        );
    }

    aggregerteOppgaverMedFristIPerioden(start: Date, slutt: Date, vedOk: (oppgaver: l.AggregerteOppgaverModell) => void = _.noop): l.AggregerteOppgaverModell {
        debug("aggregerteOppgaverMedFristIPerioden", start, slutt);
        return this.ressurs["aggregerteOppgaverMedFristIPerioden"](
            {start: start, slutt: slutt},
            vedOk,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke finne aggregerte oppgaver"))
        );
    }

    antallOppgaverPerBruker(): ng.resource.IResourceArray<l.AntallOppgaverPerBrukerModell> {
        debug("antallOppgaverPerBruker");
        return this.ressurs["antallOppgaverPerBruker"](
            {},
            _.noop,
            this.håndterFeil(() => this.gettextCatalog.getString("Kunne ikke finne antall oppgaver"))
        );
    }

    //hjelpemetoder som ikke gjør ressurskall

    /**
     * Returnerer en liste med sakers oppgaver.
     * Ett innslag per sak som har oppgaver eller beslutninger med oppgaver.
     * Oppgavene er sortert etter forfallsdato.
     */
    static sakerMedOppgaver(saker: l.AgendaelementModell[]): any[] {
        var akkumulerBeslutningsOppgaver = (akk: any[], beslutning: l.BeslutningModell) => {
            var oppgaver = _(beslutning.oppgaver)
                .map(oppgave => {
                    return {
                        oppgave: oppgave,
                        status: () => OppgaveRessurs.oppgaveStatus(oppgave),
                        beslutning: beslutning
                    }
                })
                .value();
            return akk.concat(oppgaver);
        };

        return _(saker)
            .filter(sak => sak.oppgaver.length > 0 || _(<l.BeslutningModell[]>sak.beslutninger).some(beslutning => beslutning.oppgaver.length > 0))
            .map(sak => {
                var saksOppgaver = _(<l.OppgaveModell[]>sak.oppgaver).map(oppgave => {
                    return {
                        oppgave: oppgave,
                        status: () => OppgaveRessurs.oppgaveStatus(oppgave)
                    }
                }).value();
                var beslutningsOppgaver = _(<l.BeslutningModell[]>sak.beslutninger)
                    .filter(beslutning => beslutning.oppgaver.length > 0)
                    .reduce(akkumulerBeslutningsOppgaver, []);
                return {
                    sak: sak,
                    oppgaver: _.sortBy(saksOppgaver.concat(beslutningsOppgaver), oppgave => oppgave.oppgave.frist)
                }
            })
            .value()
    }

    static oppgaveStatus(oppgave: l.OppgaveModell): string {
        if (oppgave.utfoert) return "utfoert";

        var nå = moment();
        if (!oppgave.utfoert && moment(tilLokalDatoIgnorerTs(oppgave.frist)).isSame(nå, 'days')) return "overskrides";
        if (this.erOverskredet(oppgave)) return "overskredet";

        return "ikkeoverskredet"
    }

    static antallKommentarerOgSvar(oppgave: l.OppgaveModell) :number {
        if(!oppgave.kommentarer || oppgave.kommentarer.length === 0) return 0;
        let alleSvar = _(oppgave.kommentarer).flatMap(kommentar => kommentar.svar);
        return alleSvar.size() + oppgave.kommentarer.length;
    }

    static oppgaveStatus2(oppgave: l.OppgaveModell): OppgavestatusBeregnet {
        if (oppgave.utfoert) return OppgavestatusBeregnet.utfoert;
        if (!oppgave.frist) return OppgavestatusBeregnet.ingenfrist;

        var nå = moment();
        if (!oppgave.utfoert && moment(tilLokalDatoIgnorerTs(oppgave.frist)).isSame(nå, 'days')) return OppgavestatusBeregnet.overskrides;
        if (this.erOverskredet(oppgave)) return OppgavestatusBeregnet.overskredet;

        return OppgavestatusBeregnet.ikkeoverskredet;
    }

    static erOverskredet(oppgave: l.OppgaveModell): boolean {
        var nå = moment();
        return !oppgave.utfoert && moment(tilLokalDatoIgnorerTs(oppgave.frist)).isBefore(nå, 'days');
    }
}
